<template>
  <div class="container-sercretcontroller">
    <template v-for="(card, inx) in cards" :key="`card_${card.id}`">
      <SMCSControllerCard
        v-if="!isEmpty(cards[inx])"
        v-model="cards[inx]"
        :is-selected="selectedCardId === card.id"
        :cards="cards"
        :user-preset-list="mcsStore.subUserList"
        @select="handleSelect"
        @delete="deleteCard(inx)"
      />
    </template>
    <button
      class="button-glass-dark button-icon-text button-rounded"
      :disabled="cards.length >= microphonesStore.microphones.length"
      @click="addCard"
    >
      <IconPlus /> 마이크 추가하기
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, provide, ref } from 'vue';

import { cloneDeep, isEmpty } from 'lodash';

import SMCSControllerCard from '@/components/domains/chat/mcs/SMCSControllerCard.vue';
import IconPlus from '@/components/icons/IconPlus.vue';
import { useMcsStore } from '@/stores/mcs';
import { useMicrophonesStore } from '@/stores/microphones';
import { useSocketsStore } from '@/stores/sockets';
import { useToastStore } from '@/stores/toast';
import { IMcbCard, IMicrophone } from '@/types/components';
import eventBus from '@/utils/eventBus.js';
import { eventKeys } from '@/utils/eventKeys';
import getSocketId from '@/utils/getSocketId';
import { ij_ableMicList } from '@/utils/injectionKeys';


const microphonesStore = useMicrophonesStore();
const socketsStore = useSocketsStore();
const toastStore = useToastStore();

const DEFAULT_CARD: Readonly<IMcbCard> = {
  id: getSocketId(),
  userId: '',
  nickname: '',
  microphone: {} as Pick<IMicrophone, 'id' | 'label' | 'customLabel'>,
  language: {
    code: 'en_US',
    name: 'English',
    nameInKorean: '영어',
    smallName: 'EN',
  },
};

const cards = ref<IMcbCard[]>([cloneDeep(DEFAULT_CARD)]);
const selectedCardId = ref<string>('');
const mcsStore = useMcsStore();

const ableMicList = computed<(IMicrophone & { isSelected: boolean })[]>(() => {
  const selectedMicIds = cards.value.map((card) => card.microphone?.id);
  return microphonesStore.microphones.map((microphone) => ({
    ...microphone,
    isSelected: selectedMicIds.includes(microphone.id),
  }));
});

const addCard = () => {
  cards.value.push({ ...cloneDeep(DEFAULT_CARD), id: getSocketId() });
};

const deleteCard = (inx: number) => {
  cards.value.splice(inx, 1);
};

const handleSelect = (id: string) => {
  selectedCardId.value = id;
};

// MCS 카드 저장
const saveMcsCards = () => {
  // 카드에 마이크 게인 정보 추가
  const listCardDetail = cards.value.map((card) => ({
    ...card,
    volumeRatio: microphonesStore.micSensitivityById[card.microphone.id] || 7,
  }));

  socketsStore.setListCardConnection(listCardDetail);
  toastStore.info('설정이 저장되었습니다.');
};

eventBus.on(eventKeys.MCS_SAVE, saveMcsCards);

onMounted(async () => {
  await microphonesStore.initMicrophonesStore();

  // 저장된 값이 있을 경우 자동 세팅
  if (socketsStore.listCardConnection.length > 0) {
    cards.value = [...socketsStore.listCardConnection];
  }
});

onUnmounted(async () => {
  await microphonesStore.resetMicrophoneStore();
});

provide(ij_ableMicList, ableMicList);
</script>

<style scoped lang="scss">
.container-sercretcontroller {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  width: 100%;
  height: 100%;
  padding: 0.5rem 1rem;
  gap: 0.5rem;
  background: var(--surface-gray);
  overflow-y: auto;
}

button {
  margin-bottom: 15rem;
}
</style>
